import {
  AssetDto,
  AssetType,
  DateConstant,
  ExternalAssetSearchDto,
  InstrumentDto,
  SectionAssetDto
} from '@unistocks/proxy/axios-client';
import {
  ASSET_DEFAULT_DARK,
  ASSET_DEFAULT_LIGHT,
  CASH,
  SAVINGS_ACCOUNT
} from '@unistocks/shared/consts';
import { isDefined, isNumber } from '@unistocks/shared/utils/util';
import { Asset } from '@unistocks/types/asset';
import { ImageSourcePropType } from 'react-native';
import { FlagKey, FLAGS } from '../../features/portfolios/flags';

export const isHoldings = (asset: AssetDto): boolean => !isAccount(asset) && !isLoan(asset);
export const isAccount = (asset: AssetDto): boolean =>
  asset.type === AssetType.SavingsAccount || asset.type === AssetType.Cash;
export const isLoan = (asset: AssetDto): boolean => asset.type === AssetType.Loan;
export const assetTypeHasQuantity = (assetType: AssetType) =>
  [AssetType.Stock, AssetType.Fund, AssetType.CryptoCurrency, AssetType.Other].includes(assetType);
export const assetTypeHasTotalValue = (assetType: AssetType) =>
  [AssetType.Cash, AssetType.SavingsAccount, AssetType.Loan, AssetType.Other].includes(assetType);
const isSectionAsset = (asset: Asset): asset is SectionAssetDto => 'sectionId' in asset;

export const assetValueFormatter = (asset: ExternalAssetSearchDto | InstrumentDto): string => {
  return asset && `${asset.name}${asset.symbol ? ` (${asset.symbol})` : ''}`;
};

export const formatISINAndSymbol = (asset: Asset | ExternalAssetSearchDto) =>
  Array.from(new Set([asset.isin, asset.symbol].filter(isDefined))).join(', ');

/**
 * Get the total quantity of the asset in the section.
 * If the asset is synced with external sources, the quantity is the sum of all the assets plus the quantity manually added.
 * @param asset
 * @returns section quantity
 */
export const getTotalAssetQuantity = (asset: SectionAssetDto) => {
  let quantity = asset.syncedAssets.reduce<number | null>((sum, syncedAsset) => {
    let next: number | null = sum;

    if (!syncedAsset.importSettings.isHidden) {
      if (isNumber(syncedAsset.quantity)) {
        if (!next) next = 0;
        next += syncedAsset.quantity;
      }
    }

    return next;
  }, null);

  if (isNumber(asset.quantity)) {
    if (!quantity) quantity = 0;
    quantity += asset.quantity;
  }

  return quantity;
};

/**
 * Get the fractional digits options for the asset quantity.
 * @param asset
 * @returns Intl.NumberFormatOptions
 */
export const getFractionalDigitsOptions = (asset: SectionAssetDto): Intl.NumberFormatOptions => {
  const totalQuantity = getTotalAssetQuantity(asset);
  const minimumFractionDigits = 0;
  const maximumFractionDigits = isNumber(totalQuantity) && totalQuantity < 0.5 ? 6 : 2;

  return { minimumFractionDigits, maximumFractionDigits };
};

export const getComparePrice = (asset: AssetDto, period: DateConstant): number | null => {
  switch (period) {
    case DateConstant.OneDay:
      return asset.previousPrice;

    default:
      // TODO: Fix the enum dictionary keys
      return (
        (asset.historicalPrices as unknown as Record<string, number> | null)?.[period] ??
        (asset.historicalPrices as unknown as Record<string, number> | null)?.[
          DateConstant[period]
        ] ??
        null
      );
  }
};

const marketImagePattern = /^[a-z]{2}_market/;

export type AssetImageOptions = {
  /**
   * If true, the asset image will be preferred over the market flag, or the default image of the asset type.
   * @default false
   */
  preferAssetImage?: boolean;
};

export const getAssetImage = (
  asset: Asset,
  isDarkMode: boolean,
  options?: AssetImageOptions
): ImageSourcePropType => {
  const assetDefault = isDarkMode ? ASSET_DEFAULT_DARK : ASSET_DEFAULT_LIGHT;
  const assetImageUri = isSectionAsset(asset) ? asset.assetImageUri : asset.imageUri;

  if (options?.preferAssetImage && !!assetImageUri) {
    return { uri: assetImageUri || undefined };
  }

  // Defaults for each asset type
  switch (asset.type) {
    case AssetType.Stock:
    case AssetType.Fund:
    case AssetType.Other:
    case AssetType.Index:
    case AssetType.Commodity:
      return asset.isHoldings && asset.market?.match(marketImagePattern)
        ? (FLAGS[asset.market as FlagKey] ?? assetDefault)
        : assetDefault;

    case AssetType.Cash:
      return asset.imageUri ? { uri: asset.imageUri } : CASH;

    case AssetType.SavingsAccount:
      return asset.imageUri ? { uri: asset.imageUri } : SAVINGS_ACCOUNT;

    case AssetType.CryptoCurrency:
      return isSectionAsset(asset) && asset.imageUri
        ? { uri: asset.imageUri }
        : assetImageUri
          ? { uri: assetImageUri }
          : assetDefault;

    default:
      return asset.imageUri ? { uri: asset.imageUri } : assetDefault;
  }
};
